import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchObjectives, fetchMilestoneMapsByObjective } from '../store/map3DataSlice';
import {
  startDescendantsListener,
  stopDescendantsListener,
} from '../store/collaborationThunks';
import type { RootState, AppDispatch } from '../store/store';
import type { Objective1 } from '@mm-shared/types/Objective1';

/**
 * A descendant child objective with the maps that contain it.
 */
export type DescendantChild = {
  objective: Objective1;
  maps: Array<{ shortId: string; title: string; ownerName: string }>;
};

/**
 * Hook to fetch descendant (child) objectives and their containing maps,
 * with a real-time Firestore listener for collaborative status updates.
 *
 * Pattern: hooks orchestrate thunks + read from Redux selectors.
 * Thunks handle all Firestore interaction.
 *
 * Flow:
 * 1. Dispatch `fetchObjectives` to load children into map3DataSlice.objectives
 * 2. For each child, dispatch `fetchMilestoneMapsByObjective` to load maps into
 *    map3DataSlice.milestoneMaps
 * 3. Dispatch `startDescendantsListener` to subscribe to real-time updates for
 *    the child objectives (covers descendants living in other maps, which are
 *    not covered by the existing per-map objectives listener).
 * 4. Read objectives from state.map3Data.objectives
 * 5. Read maps from state.map3Data.milestoneMaps (filter by objectiveUuids)
 * 6. On cleanup, dispatch `stopDescendantsListener` to unsubscribe.
 *
 * @param parentObjectiveUuid - UUID of the parent objective (used as listener key)
 * @param childIds - Array of child objective UUIDs to fetch
 * @returns { descendants, isLoading, error }
 */
export function useDescendants(parentObjectiveUuid: string, childIds: string[]) {
  const dispatch = useDispatch<AppDispatch>();
  const allObjectives = useSelector((state: RootState) => state.map3Data.objectives);
  const allMaps = useSelector((state: RootState) => state.map3Data.milestoneMaps);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Stabilize childIds reference to avoid infinite re-renders
  const childIdsKey = childIds.join(',');
  const stableChildIds = useRef(childIds);
  if (stableChildIds.current.join(',') !== childIdsKey) {
    stableChildIds.current = childIds;
  }

  // Stabilize parentObjectiveUuid so it can safely be used in deps
  const stableParentUuid = useRef(parentObjectiveUuid);
  if (stableParentUuid.current !== parentObjectiveUuid) {
    stableParentUuid.current = parentObjectiveUuid;
  }

  useEffect(() => {
    let isMounted = true;
    const ids = stableChildIds.current;
    const parentUuid = stableParentUuid.current;

    if (ids.length === 0) {
      setIsLoading(false);
      setError(null);
      return;
    }

    async function fetchDescendants() {
      setIsLoading(true);
      setError(null);

      try {
        // Step 1: Dispatch fetchObjectives to load children into Redux
        // fetchObjectives handles batching internally (batches of 10)
        await dispatch(fetchObjectives(ids)).unwrap();

        if (!isMounted) return;

        // Step 2: For each child, dispatch fetchMilestoneMapsByObjective
        // Results are upserted into state.map3Data.milestoneMaps (accumulating cache)
        const BATCH_SIZE = 10;
        for (let i = 0; i < ids.length; i += BATCH_SIZE) {
          if (!isMounted) return;
          const batch = ids.slice(i, i + BATCH_SIZE);
          await Promise.all(batch.map((id) => dispatch(fetchMilestoneMapsByObjective(id)).unwrap()));
        }

        if (!isMounted) return;

        // Step 3: Start real-time listener so collaborator status updates propagate
        // without requiring a page refresh. Keyed to the parent objective uuid so
        // each drawer opening gets its own listener, cleaned up on unmount.
        if (parentUuid) {
          dispatch(startDescendantsListener({ parentObjectiveUuid: parentUuid, childIds: ids }));
        }

        if (isMounted) {
          setIsLoading(false);
        }
      } catch (err) {
        if (isMounted) {
          // RTK's unwrap() can throw a serialized rejection — extract message if available
          let errorMsg = 'Unknown error';
          if (err instanceof Error) {
            errorMsg = err.message;
          } else if (typeof err === 'string') {
            errorMsg = err;
          } else if (err && typeof err === 'object' && 'message' in err) {
            errorMsg = String((err as any).message);
          }
          setError(new Error(errorMsg));
          setIsLoading(false);
        }
      }
    }

    fetchDescendants();

    return () => {
      isMounted = false;
      // Stop the real-time listener when the hook unmounts or childIds/parent changes.
      // This ensures no orphaned Firestore listeners when the drawer is closed or the
      // user navigates to a different objective.
      if (parentUuid) {
        dispatch(stopDescendantsListener({ parentObjectiveUuid: parentUuid }));
      }
    };
  }, [childIdsKey, parentObjectiveUuid, dispatch]);

  // Step 3 & 4: Build descendants from Redux state
  // Objectives come from state.map3Data.objectives
  // Maps come from state.map3Data.milestoneMaps (filtered by objectiveUuids)
  const descendants: DescendantChild[] = useMemo(() => {
    if (stableChildIds.current.length === 0) return [];

    return stableChildIds.current
      .map((id) => {
        const objective = allObjectives.find((obj) => obj.uuid === id);
        if (!objective) return null;
        const maps = allMaps
          .filter((map) => map.objectiveUuids?.includes(id))
          .map((map) => ({
            shortId: map.shortId ?? '',
            title: map.title ?? '',
            ownerName: (map as any).owner?.name ?? '',
          }));
        return {
          objective,
          maps,
        };
      })
      .filter((d): d is DescendantChild => d !== null);
  }, [allObjectives, allMaps, childIdsKey]);

  return { descendants, isLoading, error };
}
